import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Lock, CircleDot, CalendarCheck, Search, Loader2, Plus, KeyRound, Landmark, ExternalLink, Database, Save, Mail, RefreshCw, CreditCard, ChevronDown, Copy, SendHorizontal, Trash2 } from "lucide-react";
import { PageBody, PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";

const isLoom = (u) => { try { const h = new URL(u).hostname.toLowerCase(); return h === "loom.com" || h === "www.loom.com"; } catch { return false; } };

const STATUS = {
  connected: { label: "Connected", color: "text-emerald-500 bg-emerald-500/10" },
  sandbox: { label: "Sandbox", color: "text-amber-500 bg-amber-500/10" },
  not_connected: { label: "Not connected", color: "text-muted-foreground bg-muted" },
  later: { label: "Later", color: "text-muted-foreground bg-muted" },
};

// ---------------- Booking (Calendly / Cal.com) ----------------
function BookingPanel({ wsId }) {
  const [b, setB] = useState(null);
  const [url, setUrl] = useState("");
  const [key, setKey] = useState("");
  const [loom, setLoom] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { if (!wsId) return; const { data } = await api.get(`/workspaces/${wsId}/booking`); setB(data); setUrl(data.booking_url || ""); setLoom(data.default_loom_url || ""); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [wsId]);

  const saveProvider = async (patch) => { await api.put(`/workspaces/${wsId}/booking`, patch); await load(); toast.success("Booking updated"); };
  const saveUrl = async () => { await api.put(`/workspaces/${wsId}/booking`, { booking_provider: url ? "url" : "", booking_url: url }); await load(); toast.success(url ? "Fallback URL saved" : "URL cleared"); };
  const saveLoom = async () => {
    if (loom && !isLoom(loom)) return toast.error("Loom link must be a loom.com share URL");
    try { await api.put(`/workspaces/${wsId}/booking`, { default_loom_url: loom }); await load(); toast.success(loom ? "Default Loom saved" : "Loom cleared"); }
    catch { toast.error("Could not save Loom link"); }
  };
  const saveTz = async (tz) => {
    try { await api.put(`/workspaces/${wsId}/booking`, { timezone: tz }); await load(); toast.success("Timezone saved"); }
    catch { toast.error("Could not save timezone"); }
  };
  const connectCalcom = async () => {
    if (!key.trim()) return toast.error("Paste your Cal.com API key");
    setBusy(true);
    try { const { data } = await api.post(`/workspaces/${wsId}/booking/calcom/connect`, { api_key: key.trim() }); toast.success(`Cal.com connected · ${data.event_types.length} event types`); setKey(""); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not connect Cal.com"); } finally { setBusy(false); }
  };
  const disconnectCalcom = async () => { await api.post(`/workspaces/${wsId}/booking/calcom/disconnect`); await load(); toast("Cal.com disconnected"); };
  const connectCalendly = async () => {
    try { const { data } = await api.post(`/workspaces/${wsId}/booking/calendly/connect`); if (data.redirect) window.location.href = data.redirect; }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Calendly not configured yet - use the fallback URL below."); }
  };

  if (!b) return null;
  const provider = b.booking_provider || "";
  return (
    <div className="mb-8">
      <div className="eyebrow mb-3 flex items-center gap-2"><CalendarCheck className="h-3.5 w-3.5" /> Booking · this workspace</div>
      <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid="booking-panel">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-sm text-muted-foreground">Default provider</span>
          <Select value={provider || "none"} onValueChange={(v) => saveProvider({ booking_provider: v === "none" ? "" : v })}>
            <SelectTrigger className="w-[190px]" data-testid="booking-provider-select"><SelectValue placeholder="Choose provider" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="calendly">Calendly</SelectItem>
              <SelectItem value="calcom">Cal.com</SelectItem>
              <SelectItem value="url">Manual URL</SelectItem>
            </SelectContent>
          </Select>
          {b.resolved_book_link
            ? <Badge className="bg-emerald-500/15 text-emerald-500 border-0" data-testid="booking-ready"><Check className="h-3 w-3 mr-1" /> Book CTA ready</Badge>
            : <Badge variant="outline" className="text-muted-foreground" data-testid="booking-blocked">Book CTA blocked - add a link</Badge>}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Calendly */}
          <div className="rounded-lg border border-border/60 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">Calendly</span>
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${b.calendly_connected ? STATUS.connected.color : STATUS.not_connected.color}`}>{b.calendly_connected ? "Connected" : "Not connected"}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{b.calendly_configured ? "Connect your Calendly account via OAuth, then pick an event type." : "OAuth not configured yet. Use the fallback URL below, or add CALENDLY_CLIENT_ID to enable official OAuth."}</p>
            <Button size="sm" variant="outline" className="rounded-md" onClick={connectCalendly} data-testid="connect-calendly">Connect Calendly</Button>
          </div>

          {/* Cal.com */}
          <div className="rounded-lg border border-border/60 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">Cal.com</span>
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${b.calcom_connected ? STATUS.connected.color : STATUS.not_connected.color}`}>{b.calcom_connected ? "Connected" : "Not connected"}</span>
            </div>
            {b.calcom_connected ? (
              <>
                <p className="text-xs text-emerald-500 font-mono mb-2">{b.calcom_owner}</p>
                <Label className="text-xs">Default event type</Label>
                <Select value={b.default_event_type_id || ""} onValueChange={(v) => saveProvider({ booking_provider: "calcom", default_event_type_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="calcom-event-type"><SelectValue placeholder="Pick event type" /></SelectTrigger>
                  <SelectContent>{b.calcom_event_types.map((e) => <SelectItem key={e.id} value={e.id}>{e.title} · {e.length}min</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="ghost" className="rounded-md mt-2 text-rose-500" onClick={disconnectCalcom} data-testid="disconnect-calcom">Disconnect</Button>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-2">Paste your Cal.com API key to fetch event types (official API).</p>
                <div className="flex gap-2">
                  <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="cal_live_…" className="text-xs" data-testid="calcom-key-input" />
                  <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400 shrink-0" onClick={connectCalcom} disabled={busy} data-testid="calcom-connect-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}</Button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs">Fallback booking URL</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://cal.com/you/30min or Calendly link" data-testid="booking-url-input" />
            <Button size="sm" variant="outline" className="rounded-md shrink-0" onClick={saveUrl} data-testid="save-booking-url">Save URL</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">The reply inbox "Book" uses this workspace's default provider only. No URL is invented - if none is set, Book is blocked.</p>
        </div>

        <div className="mt-4 pt-4 border-t border-border/50">
          <Label className="text-xs">Default Loom walkthrough URL (this workspace)</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={loom} onChange={(e) => setLoom(e.target.value)} placeholder="https://www.loom.com/share/…" data-testid="loom-default-input"
              className={loom && !isLoom(loom) ? "border-rose-500" : ""} />
            <Button size="sm" variant="outline" className="rounded-md shrink-0" onClick={saveLoom} data-testid="save-loom-default">Save Loom</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Appended to email steps as a "20-second walkthrough:" line, unless a step or contact sets its own. 15 to 30 seconds; one problem, one ask.</p>
        </div>

        <div className="mt-4 pt-4 border-t border-border/50">
          <Label className="text-xs">Workspace timezone (scheduling)</Label>
          <Select value={b.timezone || "America/New_York"} onValueChange={saveTz}>
            <SelectTrigger className="mt-1.5 w-[260px]" data-testid="ws-timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Singapore", "Australia/Sydney", "UTC"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">Scheduled email sends, LinkedIn due dates, and social times are interpreted and shown in this timezone.</p>
        </div>
      </div>
    </div>
  );
}

// ---------------- Email senders: Layer A (connect mailbox) + Layer B (BYO sending app) ----------------
function ByoAppForm({ provider, cfg, onSaved }) {
  const isGoogle = provider === "google";
  const [clientId, setClientId] = useState(cfg.client_id || "");
  const [secret, setSecret] = useState("");
  const [tenant, setTenant] = useState(cfg.tenant_id || "common");
  const [replacing, setReplacing] = useState(!cfg.secret_saved);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { setClientId(cfg.client_id || ""); setTenant(cfg.tenant_id || "common"); setReplacing(!cfg.secret_saved); setSecret(""); }, [cfg.client_id, cfg.tenant_id, cfg.secret_saved]);

  const copyUri = () => { navigator.clipboard.writeText(cfg.redirect_uri); toast.success("Redirect URI copied"); };
  const save = async () => {
    if (!clientId.trim()) return toast.error("Client ID is required");
    setBusy(true);
    try {
      const body = { client_id: clientId.trim() };
      if (replacing && secret.trim()) body.client_secret = secret.trim();
      if (!isGoogle) body.tenant_id = tenant.trim() || "common";
      await api.put(`/integrations/sender-apps/${provider}`, body);
      toast.success("Sending app saved");
      setSecret(""); await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not save"); } finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const { data } = await api.post(`/integrations/sender-apps/${provider}/test`); data.ok ? toast.success(data.message) : toast.error(data.message); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Test failed"); } finally { setTesting(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await api.delete(`/integrations/sender-apps/${provider}`); toast("Sending app cleared"); await onSaved(); }
    catch { toast.error("Could not clear"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border/60 p-4" data-testid={`byo-form-${provider}`}>
      <div className="text-sm font-medium mb-3">{isGoogle ? "Google" : "Microsoft"}</div>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">{isGoogle ? "Client ID" : "Application (client) ID"}</Label>
          <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder={isGoogle ? "…apps.googleusercontent.com" : "00000000-0000-0000-0000-000000000000"} className="mt-1.5 text-xs font-mono" data-testid={`byo-${provider}-client-id`} />
        </div>
        <div>
          <Label className="text-xs">Client secret</Label>
          {cfg.secret_saved && !replacing ? (
            <div className="flex items-center gap-2 mt-1.5">
              <Badge className="bg-emerald-500/15 text-emerald-500 border-0" data-testid={`byo-${provider}-secret-saved`}><Check className="h-3 w-3 mr-1" /> Secret saved</Badge>
              <Button size="sm" variant="ghost" className="rounded-md h-7 text-xs" onClick={() => setReplacing(true)} data-testid={`byo-${provider}-replace-secret`}>Replace</Button>
            </div>
          ) : (
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Paste secret (write-only)" className="mt-1.5 text-xs font-mono" data-testid={`byo-${provider}-client-secret`} />
          )}
        </div>
        {!isGoogle && (
          <div>
            <Label className="text-xs">Directory (tenant) ID <span className="text-muted-foreground">· optional</span></Label>
            <Input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="common" className="mt-1.5 text-xs font-mono" data-testid={`byo-${provider}-tenant`} />
          </div>
        )}
        <div>
          <Label className="text-xs">Authorized redirect URI</Label>
          <div className="flex gap-2 mt-1.5">
            <Input readOnly value={cfg.redirect_uri} className="text-xs font-mono bg-muted/50" data-testid={`byo-${provider}-redirect-uri`} />
            <Button size="sm" variant="outline" className="rounded-md shrink-0" onClick={copyUri} data-testid={`byo-${provider}-copy-uri`}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            {isGoogle
              ? "In Google Cloud, enable the Gmail API. Add this exact redirect URI. Scopes: gmail.send and gmail.readonly."
              : "In Azure, expose Microsoft Graph Mail.Send and Mail.Read. Add this exact redirect URI."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={save} disabled={busy} data-testid={`byo-${provider}-save`}><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button>
          <Button size="sm" variant="outline" className="rounded-md" onClick={test} disabled={testing || !cfg.byo} data-testid={`byo-${provider}-test`}>{testing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />} Test app</Button>
          {cfg.byo && <Button size="sm" variant="ghost" className="rounded-md text-rose-500" onClick={clear} disabled={busy} data-testid={`byo-${provider}-clear`}><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear app</Button>}
        </div>
      </div>
    </div>
  );
}

function SignatureEditor({ which, meta, onSave }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(meta.signature || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await onSave(which, val); toast.success("Signature saved"); setOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not save signature"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 w-full" data-testid={`signature-wrap-${which}`}>
      {!open ? (
        <button onClick={() => { setVal(meta.signature || ""); setOpen(true); }} className="text-[11px] text-muted-foreground hover:text-foreground underline" data-testid={`signature-edit-${which}`}>
          {meta.signature ? "Edit signature" : "Add signature"}
        </button>
      ) : (
        <div className="rounded-lg border border-border/60 bg-card/40 p-3">
          <Label className="text-[11px]">Signature · appended after every send</Label>
          <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3} className="mt-1.5 text-sm" placeholder={meta.signature_default || "Your name\nyou@company.com"} data-testid={`signature-input-${which}`} />
          <p className="text-[10px] text-muted-foreground mt-1">Plain text or simple HTML. Leave empty to use the default: your name + this mailbox email.</p>
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={save} disabled={busy} data-testid={`signature-save-${which}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save signature"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`signature-cancel-${which}`}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailSendersPanel({ wsId }) {
  const [s, setS] = useState(null);
  const [apps, setApps] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [testingBox, setTestingBox] = useState("");
  const [checking, setChecking] = useState("");
  const [byoOpen, setByoOpen] = useState(false);
  const [modal, setModal] = useState(null); // "gmail" | "outlook"
  const [resumeModal, setResumeModal] = useState(null); // { which, count }
  const [resuming, setResuming] = useState(false);

  const load = async () => { const { data } = await api.get("/integrations/senders"); setS(data); return data; };
  const loadApps = async () => { const { data } = await api.get("/integrations/sender-apps"); setApps(data); };
  const verifyAll = async (data) => {
    const jobs = [];
    if (data.gmail.connected) jobs.push(api.post("/integrations/gmail/verify").catch(() => {}));
    if (data.outlook.connected) jobs.push(api.post("/integrations/outlook/verify").catch(() => {}));
    if (jobs.length) { await Promise.all(jobs); await load(); }
  };
  useEffect(() => { (async () => { const d = await load(); await loadApps(); verifyAll(d); })(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtVerified = (iso) => {
    if (!iso) return null;
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  };
  const recheck = async (which) => {
    setChecking(which);
    try { await api.post(`/integrations/${which}/verify`); await load(); toast.success("Mailbox re-checked"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Re-check failed"); } finally { setChecking(""); }
  };
  const runResume = async () => {
    if (!resumeModal) return;
    setResuming(true);
    try {
      const { data } = await api.post(`/integrations/${resumeModal.which}/resume-blocked`);
      toast.success(`Resuming ${data.resumed} paused send${data.resumed === 1 ? "" : "s"}`);
      setResumeModal(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not resume"); } finally { setResuming(false); }
  };

  const connect = (which) => {
    const meta = which === "gmail" ? s.gmail : s.outlook;
    if (!meta.configured) { setModal(which); return; }
    window.location.href = `${API}/oauth/${which}/login`;
  };
  const disconnect = async (which) => { await api.post(`/integrations/${which}/disconnect`); await load(); toast(`${which === "gmail" ? "Google" : "Outlook"} disconnected`); };
  const saveSignature = async (which, signature) => { await api.put(`/integrations/${which}/signature`, { signature }); await load(); };
  const chooseSender = async (sender) => {
    try { await api.put(`/workspaces/${wsId}/active-sender`, { sender }); await load(); toast.success(`Active sender: ${sender === "gmail" ? "Google" : "Outlook"}`); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not set active sender"); }
  };
  const sendTest = async (which) => {
    setTestingBox(which);
    try { const { data } = await api.post(`/integrations/${which}/test-mailbox`); data.sent ? toast.success(data.message) : toast(data.message); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not send test"); } finally { setTestingBox(""); }
  };
  const syncReplies = async () => {
    setSyncing(true);
    let total = 0;
    try {
      if (s?.gmail?.connected) { const { data } = await api.post("/integrations/gmail/sync-replies"); total += data.imported || 0; }
      if (s?.outlook?.connected) { const { data } = await api.post("/integrations/outlook/sync-replies"); total += data.imported || 0; }
      toast.success(`Reply inbox synced · ${total} new`);
    } catch { toast.error("Reply sync failed"); } finally { setSyncing(false); }
  };

  if (!s) return null;
  const both = s.gmail.connected && s.outlook.connected;
  const canManage = apps?.can_manage;

  const Card = ({ which, meta, name }) => {
    const errored = meta.connected && meta.health === "error";
    const pill = errored
      ? "text-rose-500 bg-rose-500/10"
      : meta.connected ? STATUS.connected.color : STATUS.not_connected.color;
    return (
    <div className="rounded-lg border border-border/60 p-4" data-testid={`sender-card-${which}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-sm flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {name}</span>
        <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${pill}`} data-testid={`sender-status-${which}`}>{errored ? "Error" : meta.connected ? "Connected" : "Not connected"}</span>
      </div>
      {meta.connected ? (
        <>
          <p className="text-xs text-emerald-500 font-mono mb-1" data-testid={`sender-email-${which}`}>{meta.email}</p>
          {errored
            ? <p className="text-[11px] text-rose-500 mb-3" data-testid={`sender-health-${which}`}>{meta.health_reason || "Authorization expired or revoked."} Live send is blocked for this mailbox.</p>
            : <p className="text-[11px] text-muted-foreground mb-3" data-testid={`sender-health-${which}`}>{meta.last_verified_at ? `Verified ${fmtVerified(meta.last_verified_at)}` : (checking === which ? "Checking…" : "Not verified yet")}</p>}
        </>
      ) : (
        <p className="text-xs text-muted-foreground mb-3">Connect a mailbox to send approved mail and ingest replies.</p>
      )}
      {meta.connected && s.active_sender === which && <Badge className="bg-amber-500/15 text-amber-500 border-0 mb-3" data-testid={`active-sender-${which}`}>Active sender</Badge>}
      <div className="flex flex-wrap gap-2">
        {meta.connected ? (
          errored ? (
            <>
              <Button size="sm" variant="outline" className="rounded-md" onClick={() => connect(which)} data-testid={`reconnect-${which}`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reconnect</Button>
              <Button size="sm" variant="ghost" className="rounded-md text-rose-500" onClick={() => disconnect(which)} data-testid={`disconnect-${which}`}>Disconnect</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" className="rounded-md" onClick={() => sendTest(which)} disabled={testingBox === which} data-testid={`test-mailbox-${which}`}>{testingBox === which ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <SendHorizontal className="h-3.5 w-3.5 mr-1.5" />} Send test to me</Button>
              <Button size="sm" variant="ghost" className="rounded-md" onClick={() => recheck(which)} disabled={checking === which} data-testid={`recheck-${which}`}>{checking === which ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Re-check</Button>
              {meta.blocked_count > 0 && <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={() => setResumeModal({ which, count: meta.blocked_count })} data-testid={`resume-blocked-${which}`}><SendHorizontal className="h-3.5 w-3.5 mr-1.5" /> Resume paused sends ({meta.blocked_count})</Button>}
              <Button size="sm" variant="ghost" className="rounded-md text-rose-500" onClick={() => disconnect(which)} data-testid={`disconnect-${which}`}>Disconnect</Button>
            </>
          )
        ) : (
          <Button size="sm" variant="outline" className="rounded-md" onClick={() => connect(which)} data-testid={`connect-${which}`}>{which === "gmail" ? "Connect Google" : "Connect Outlook"}</Button>
        )}
      </div>
      {meta.connected && !errored && canManage && <SignatureEditor which={which} meta={meta} onSave={saveSignature} />}
    </div>
    );
  };

  return (
    <div className="mb-8">
      <div className="eyebrow mb-3 flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> Email senders · this workspace</div>
      <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid="email-senders-panel">
        <div className="grid md:grid-cols-2 gap-4">
          <Card which="gmail" meta={s.gmail} name="Google Workspace (send + reply)" />
          <Card which="outlook" meta={s.outlook} name="Outlook / Microsoft 365 (send + reply)" />
        </div>

        {both && (
          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid="sender-chooser">
            <div className="text-sm font-medium mb-1">Two mailboxes are connected — which one sends?</div>
            <p className="text-xs text-muted-foreground mb-3">One active sender per workspace. Approved and scheduled mail goes out from this mailbox only.</p>
            <div className="flex gap-2">
              <Button size="sm" variant={s.active_sender === "gmail" ? "default" : "outline"} className="rounded-md" onClick={() => chooseSender("gmail")} data-testid="choose-gmail">Google Workspace</Button>
              <Button size="sm" variant={s.active_sender === "outlook" ? "default" : "outline"} className="rounded-md" onClick={() => chooseSender("outlook")} data-testid="choose-outlook">Outlook</Button>
            </div>
          </div>
        )}

        {(s.gmail.connected || s.outlook.connected) && (
          <div className="mt-4 flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-md" onClick={syncReplies} disabled={syncing} data-testid="sync-replies-btn">
              {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Sync replies
            </Button>
            <span className="text-[11px] text-muted-foreground">Pulls recent inbox replies into the Reply Inbox. Sending still honors approval, send_at, live_send, sandbox blocks and quota.</span>
          </div>
        )}

        {/* Layer B — BYO sending app (Owner / Admin only) */}
        {canManage && apps && (
          <Collapsible open={byoOpen} onOpenChange={setByoOpen} className="mt-5 pt-5 border-t border-border/50">
            <CollapsibleTrigger className="flex w-full items-center justify-between text-left" data-testid="byo-toggle">
              <div>
                <div className="text-sm font-medium">Your Google / Microsoft app <span className="text-muted-foreground font-normal">· optional</span></div>
                <p className="text-[11px] text-muted-foreground mt-0.5">Most teams skip this and just click Connect. Use this only if your IT team issued its own OAuth app.</p>
              </div>
              <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${byoOpen ? "rotate-180" : ""}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <ByoAppForm provider="google" cfg={apps.google} onSaved={async () => { await loadApps(); await load(); }} />
                <ByoAppForm provider="microsoft" cfg={apps.microsoft} onSaved={async () => { await loadApps(); await load(); }} />
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="sender-not-configured-modal">
          <DialogHeader>
            <DialogTitle>Sending app not configured</DialogTitle>
            <DialogDescription>
              {canManage
                ? "No OAuth app is set up for this workspace yet. Open “Your Google / Microsoft app” below and add your app, then click Connect."
                : "This workspace can’t connect a mailbox yet. Ask an Admin to finish setting up the sending app."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {canManage && <Button className="rounded-md" onClick={() => { setModal(null); setByoOpen(true); }} data-testid="modal-open-byo">Open app settings</Button>}
            <Button variant="outline" className="rounded-md" onClick={() => setModal(null)} data-testid="modal-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resumeModal} onOpenChange={(o) => !o && setResumeModal(null)}>
        <DialogContent data-testid="resume-blocked-modal">
          <DialogHeader>
            <DialogTitle>Resume paused sends?</DialogTitle>
            <DialogDescription>
              {resumeModal?.count} send{resumeModal?.count === 1 ? " was" : "s were"} paused because this mailbox needed reconnecting. Resuming re-queues them now — each still honors approval, its scheduled time, live-send, and suppression before going out.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={runResume} disabled={resuming} data-testid="resume-confirm">{resuming ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Resume {resumeModal?.count} send{resumeModal?.count === 1 ? "" : "s"}</Button>
            <Button variant="outline" className="rounded-md" onClick={() => setResumeModal(null)} data-testid="resume-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Billing (need now) ----------------
function BillingCard() {
  const navigate = useNavigate();
  return (
    <div className="mb-8">
      <div className="eyebrow mb-3 flex items-center gap-2"><CreditCard className="h-3.5 w-3.5" /> Billing</div>
      <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid="billing-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-sm">Stripe</div>
            <p className="text-xs text-muted-foreground mt-1">Plan, seats, and invoices for this organization.</p>
          </div>
          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${STATUS.connected.color}`}>Connected</span>
        </div>
        <Button size="sm" variant="outline" className="rounded-md mt-4" onClick={() => navigate("/app/admin/billing")} data-testid="open-billing">Open billing</Button>
      </div>
    </div>
  );
}

// ---------------- Apollo (sandbox search) ----------------
function ApolloPanel() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return toast.error("Enter a search (e.g. RevOps leaders in SaaS)");
    setBusy(true);
    try { const { data } = await api.post("/integrations/apollo/search", { query: q.trim() }); setRes(data); }
    catch { toast.error("Search failed"); } finally { setBusy(false); }
  };
  const importOne = async (p) => {
    try { await api.post("/contacts/import", { rows: [{ name: p.name, title: p.title, company: p.company, email: p.email, fit_score: p.fit_score }], source: "apollo" }); toast.success(`${p.name} added to leads`); }
    catch { toast.error("Could not import"); }
  };

  return (
    <div className="mb-8">
      <div className="eyebrow mb-3">Apollo · sandbox search</div>
      <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid="apollo-panel">
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="e.g. RevOps leaders at mid-market SaaS" data-testid="apollo-search-input" />
          <Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400 shrink-0" onClick={search} disabled={busy} data-testid="apollo-search-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
        </div>
        {res && (
          <>
            <p className="text-xs text-amber-500 mt-3" data-testid="apollo-notice">{res.notice}</p>
            <div className="mt-3 space-y-2">
              {res.results.map((p, i) => (
                <div key={p.email || `${p.name}-${i}`} className="flex items-center justify-between rounded-lg border border-border/60 p-3" data-testid="apollo-result-row">
                  <div><div className="text-sm font-medium">{p.name} <span className="text-muted-foreground font-normal">· {p.title}</span></div><div className="text-xs text-muted-foreground font-mono">{p.company} · {p.email}</div></div>
                  <Button size="sm" variant="outline" className="rounded-md" onClick={() => importOne(p)} data-testid="apollo-import-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                </div>
              ))}
            </div>
          </>
        )}
        {!res && <p className="text-xs text-muted-foreground mt-3">Sandbox mode: results are illustrative, never billed, and never mixed into live sends. Imported contacts still require enrollment + first-touch approval before anything sends.</p>}
      </div>
    </div>
  );
}

// ---------------- Public-sector data (SAM.gov / USASpending sandbox) ----------------
function PublicDataPanel() {
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("samgov");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return toast.error("Enter a search (e.g. professional services acquisitions)");
    setBusy(true);
    try { const { data } = await api.post("/integrations/public/search", { query: q.trim(), source: src }); setRes(data); }
    catch { toast.error("Search failed"); } finally { setBusy(false); }
  };
  const importOne = async (p) => {
    try {
      await api.post("/contacts/import", { rows: [{ name: p.name, title: p.role, role: p.role, company: p.company, agency: p.agency, department: p.department, procurement_category: p.procurement_category, email: p.email, source_url: p.source_url, fit_score: p.fit_score }], source: "sandbox_public" });
      toast.success(`${p.name} added to leads (sandbox - cannot live-send)`);
    } catch { toast.error("Could not import"); }
  };

  return (
    <div className="mb-8">
      <div className="eyebrow mb-3 flex items-center gap-2"><Landmark className="h-3.5 w-3.5" /> Public sector · official records</div>
      <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid="public-data-panel">
        <div className="flex flex-wrap gap-2">
          <Select value={src} onValueChange={setSrc}>
            <SelectTrigger className="w-[190px]" data-testid="public-source-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="samgov">SAM.gov (entity records)</SelectItem>
              <SelectItem value="usaspending">USASpending.gov (awards)</SelectItem>
            </SelectContent>
          </Select>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="e.g. professional services acquisitions" className="flex-1 min-w-[180px]" data-testid="public-search-input" />
          <Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400 shrink-0" onClick={search} disabled={busy} data-testid="public-search-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
        </div>
        {res && (
          <>
            <p className="text-xs text-amber-500 mt-3" data-testid="public-notice">{res.notice}</p>
            <div className="mt-3 space-y-2">
              {res.results.map((p, i) => (
                <div key={p.email || `${p.name}-${i}`} className="flex items-center justify-between rounded-lg border border-border/60 p-3 gap-3" data-testid="public-result-row">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.name} <span className="text-muted-foreground font-normal">· {p.role}</span></div>
                    <div className="text-xs text-sky-500 truncate">{p.agency} · {p.department}</div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">{p.procurement_category} · {p.email}</div>
                    <a href={p.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground hover:text-amber-500 inline-flex items-center gap-1 mt-0.5" data-testid="public-source-link"><ExternalLink className="h-3 w-3" /> Open source</a>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-md shrink-0" onClick={() => importOne(p)} data-testid="public-import-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                </div>
              ))}
            </div>
          </>
        )}
        {!res && <p className="text-xs text-muted-foreground mt-3">No API key yet, so this is a sandbox of official-looking public records. Each carries a stored source URL. Sandbox public contacts can never live-send. Add SAM.gov / USASpending keys to pull real records.</p>}
      </div>
    </div>
  );
}

// ---------------- HubSpot (OAuth connection screen + sandbox field mapping) ----------------
function HubSpotPanel() {
  const [h, setH] = useState(null);
  const [mapping, setMapping] = useState({});
  const [sync, setSync] = useState(false);

  const load = async () => { const { data } = await api.get("/integrations/hubspot"); setH(data); setMapping(data.mapping || {}); setSync(!!data.sync_deal_stage); };
  useEffect(() => { load(); }, []);

  const save = async () => { await api.put("/integrations/hubspot", { mapping, sync_deal_stage: sync }); toast.success("Field mapping saved"); load(); };
  const connect = async () => {
    try { const { data } = await api.post("/integrations/hubspot/connect"); if (data.redirect) window.location.href = data.redirect; }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "HubSpot OAuth is not configured yet - map fields against sandbox objects below."); }
  };

  if (!h) return null;
  return (
    <div className="mb-8">
      <div className="eyebrow mb-3 flex items-center gap-2"><Database className="h-3.5 w-3.5" /> HubSpot · two-way sync</div>
      <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid="hubspot-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">HubSpot</span>
            <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${h.connected ? STATUS.connected.color : STATUS.not_connected.color}`} data-testid="hubspot-status">{h.connected ? "Connected" : "Not connected"}</span>
            {h.mode === "sandbox" && <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-500/40" data-testid="hubspot-sandbox-badge">Sandbox</Badge>}
          </div>
          <Button size="sm" variant="outline" className="rounded-md" onClick={connect} data-testid="connect-hubspot">Connect HubSpot</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{h.configured ? "OAuth is configured. Connect to enable two-way contact and deal sync." : "OAuth not configured yet (no client id / secret). Map fields against sandbox objects now - nothing syncs live until keys exist. DealsOps stays the source of truth for outbound activity."}</p>

        <div className="rounded-lg border border-border/60 p-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3">Field mapping · sandbox objects</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {["name", "email", "title", "company", "agency"].map((f) => (
              <div key={f} className="flex items-center gap-2">
                <span className="text-xs w-20 shrink-0 text-muted-foreground capitalize">{f}</span>
                <span className="text-muted-foreground">→</span>
                <Input value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })} className="text-xs" placeholder="HubSpot property" data-testid={`hs-map-${f}`} />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
            <div>
              <div className="text-sm font-medium">HubSpot keeps deal stage</div>
              <div className="text-xs text-muted-foreground">If on, HubSpot owns the deal stage. DealsOps remains source of truth for outbound activity.</div>
            </div>
            <Switch checked={sync} onCheckedChange={setSync} data-testid="hubspot-deal-stage-toggle" />
          </div>
          <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400 mt-4" onClick={save} data-testid="hubspot-save-mapping"><Save className="h-3.5 w-3.5 mr-1.5" /> Save mapping</Button>
        </div>
      </div>
    </div>
  );
}

export default function Integrations() {
  const { user } = useAuth();
  const wsId = user?.active_workspace_id;
  const [items, setItems] = useState([]);
  const [gmail, setGmail] = useState(null);
  const [flags, setFlags] = useState({ public_sector: true, hubspot_card: false, navigator_import: true });
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/integrations").then((r) => setItems(r.data)).catch(() => {});
    api.get("/integrations/gmail/status").then((r) => setGmail(r.data)).catch(() => {});
    api.get("/org/status").then((r) => setFlags(r.data.feature_flags || flags)).catch(() => {});
    const g = params.get("gmail");
    if (g === "connected") { toast.success("Google connected — approved emails now send live."); params.delete("gmail"); setParams(params); }
    else if (g === "error") { toast.error("Couldn't connect. Check the app settings with an Admin."); params.delete("gmail"); setParams(params); }
    const o = params.get("outlook");
    if (o === "connected") { toast.success("Outlook connected — approved emails can now send from Microsoft 365."); params.delete("outlook"); setParams(params); }
    else if (o === "error") { toast.error("Couldn't connect. Check the app settings with an Admin."); params.delete("outlook"); setParams(params); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = (c) => {
    if (c.connect_action === "gmail") {
      if (gmail && !gmail.configured) { toast.error("Gmail not configured yet", { description: "Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET, then connect." }); return; }
      window.location.href = `${API}/oauth/gmail/login`;
      return;
    }
    toast("Connection screen", { description: `${c.name} connects in its delivery phase. No live data until keys exist.` });
  };
  const disconnectGmail = async () => { await api.post("/integrations/gmail/disconnect"); const r = await api.get("/integrations/gmail/status"); setGmail(r.data); api.get("/integrations").then((res) => setItems(res.data)); toast("Gmail disconnected"); };

  // Apollo + booking have dedicated panels above; hide their generic cards
  const gridItems = items.filter((c) => !["apollo", "calcom", "samgov", "usaspending", "hubspot", "google_workspace", "stripe"].includes(c.key));
  const groups = gridItems.reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {});
  const LATER = ["navigator"];

  return (
    <PageBody>
      <PageHeader eyebrow="Integrations" title="Official APIs & licensed data only"
        subtitle="Workspace-scoped connectors. Nothing shows Connected without a real token or saved URL. No unofficial LinkedIn automation - ever." />
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mb-6 text-xs text-muted-foreground flex items-start gap-2">
        <Lock className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
        <span>DealsOps never scrapes LinkedIn or bypasses Apollo, LinkedIn, or government terms. Sales Navigator requires your own license (CSV/export ingest only). Public-sector data uses official directories with a stored source URL.</span>
      </div>

      <h2 className="font-display text-xl font-semibold tracking-tight mb-4" data-testid="section-need-now">Need now</h2>
      <EmailSendersPanel wsId={wsId} />
      <BookingPanel wsId={wsId} />
      <BillingCard />

      <h2 className="font-display text-xl font-semibold tracking-tight mb-4 mt-10" data-testid="section-advanced">Advanced · set up later</h2>
      <ApolloPanel />
      {flags.public_sector && <PublicDataPanel />}
      {flags.hubspot_card && <HubSpotPanel />}

      {Object.entries(groups).map(([cat, list]) => (
        <div key={cat} className="mb-8">
          <div className="eyebrow mb-3">{cat}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((c) => {
              const isLater = LATER.includes(c.key);
              const st = isLater ? STATUS.later : (STATUS[c.status] || STATUS.not_connected);
              const isGmail = c.connect_action === "gmail";
              return (
                <div key={c.key} className={`rounded-xl border bg-card/50 p-5 ${(!c.available || isLater) ? "opacity-60" : "border-border/70"}`} data-testid={`integration-${c.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">{c.category}</span>
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${st.color}`}>{st.label}</span>
                  </div>
                  <div className="font-medium text-sm">{c.name}</div>
                  {isGmail && gmail?.connected && <p className="text-xs text-emerald-500 mt-1.5 font-mono">{gmail.email}</p>}
                  {isGmail && !gmail?.connected && !gmail?.configured && <p className="text-xs text-amber-500 mt-1.5">Add Google credentials to enable live sending.</p>}
                  {isLater && <p className="text-xs text-muted-foreground mt-1.5">Planned for a later phase.</p>}
                  {c.note && !isLater && <p className="text-xs text-muted-foreground mt-1.5">{c.note}</p>}
                  <div className="mt-4 flex gap-2">
                    {isLater ? (
                      <Badge variant="outline" className="text-xs"><Lock className="h-3 w-3 mr-1" /> Later</Badge>
                    ) : !c.available ? (
                      <Badge variant="outline" className="text-xs"><Lock className="h-3 w-3 mr-1" /> Upgrade to unlock</Badge>
                    ) : isGmail && gmail?.connected ? (
                      <Button size="sm" variant="outline" className="rounded-md" onClick={disconnectGmail} data-testid="disconnect-gmail">Disconnect</Button>
                    ) : c.status === "connected" ? (
                      <Button size="sm" variant="outline" className="rounded-md" disabled>Manage</Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-md" onClick={() => connect(c)} data-testid={`connect-${c.key}`}>Connect</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </PageBody>
  );
}
